import { useEffect, useState } from "react";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { searchDemoDictionaryApi } from "../../vocab-map/services/vocabMap.services";
import ramsaLogoShort from "../../assets/logos/ramsa-logo-short-colored.svg";

const HERO_BACKGROUND = {
  backgroundImage: "linear-gradient(160deg, #7a251f 0%, #9b2f28 45%, #c45a4e 100%)",
  clipPath: "polygon(0 0, 100% 0, 100% 100%, 0 86%)",
};

const TOPBAR_BACKGROUND = {
  backgroundImage: "linear-gradient(160deg, #7a251f 0%, #9b2f28 45%, #c45a4e 100%)",
};

const SEARCH_EXAMPLES = [
  { query: "يضُمّ", hint: "to_hold_on_to_sth_and/or_someone" },
  { query: "يِرْبَع", hint: "run" },
  { query: "رِبِيع", hint: "friend" },
  { query: "يرمس", hint: "to_speak" },
];

export function formatExampleHint(hint) {
  return String(hint || "")
    .split(";")
    .map((part) => part.replace(/_/g, " ").trim())
    .filter(Boolean)
    .join(" · ");
}

function SuggestionDropdown({ results, onSelect }) {
  if (!results.length) return null;
  return (
    <ul className="absolute left-0 right-0 z-20 mt-2 max-h-80 overflow-auto rounded-xl bg-white py-1 text-left text-gray-900 shadow-lg">
      {results.map((entry, index) => {
        const firstSense = (entry.senses && entry.senses[0]) || "";
        return (
          <li key={`${entry.lex}-${entry.pos}-${index}`}>
            <button
              type="button"
              onClick={() => onSelect(entry)}
              className="flex w-full items-baseline gap-3 px-4 py-2 text-left hover:bg-gray-50"
            >
              <span className="text-lg" dir="rtl" style={{ fontFamily: "var(--font-arabic-title)" }}>
                {entry.lex || "—"}
              </span>
              <span className="min-w-0 truncate text-sm text-gray-600">
                {entry.pos && <span className="mr-2 italic lowercase">{entry.pos}</span>}
                {firstSense}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

export function DemoDictionaryChrome({ variant = "hero", children }) {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const isHero = variant === "hero";

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(searchQuery.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const searchResult = useQuery({
    queryKey: ["demo-dictionary-search", debouncedQuery],
    queryFn: () => searchDemoDictionaryApi({ query: debouncedQuery, limit: 8 }),
    enabled: debouncedQuery.length >= 1,
    refetchOnWindowFocus: false,
  });

  const results = debouncedQuery ? searchResult.data || [] : [];
  const showDropdown = searchQuery.trim().length >= 1;

  const openWord = (entry) => {
    if (!entry?.lex) return;
    const params = new URLSearchParams({ lex: entry.lex });
    navigate(`/demo_dictionary/word?${params.toString()}`);
  };

  const submitSearch = (event) => {
    event.preventDefault();
    if (results[0]) openWord(results[0]);
  };

  const searchField = (
    <form onSubmit={submitSearch} className={`relative z-20 ${isHero ? "mx-auto mt-6 max-w-2xl" : "min-w-0 flex-1"}`}>
      <div className="flex items-center rounded-full bg-white p-1.5 shadow">
        <MagnifyingGlassIcon className="ml-3 h-5 w-5 shrink-0 text-gray-400" />
        <input
          type="search"
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          placeholder="Search a word or phrase"
          className="min-w-0 flex-1 appearance-none border-0 bg-transparent px-3 py-2 text-sm text-gray-900 shadow-none outline-none [&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-decoration]:appearance-none"
        />
        <button
          type="submit"
          className="rounded-full bg-ramsalab-primary px-5 py-2 text-sm font-semibold text-white hover:bg-ramsalab-primary-dark"
        >
          Search
        </button>
      </div>
      {showDropdown && (
        <SuggestionDropdown results={results} onSelect={openWord} />
      )}
    </form>
  );

  return (
    <div className="min-h-screen bg-white text-gray-900 dark:bg-ramsalab-deep-navy dark:text-gray-100">
      {isHero ? (
        <section className="relative isolate overflow-visible px-4 pb-24 pt-6 text-center text-white">
          <div aria-hidden="true" className="absolute inset-0 z-0" style={HERO_BACKGROUND} />
          <div className="relative z-10 mx-auto flex max-w-5xl justify-start">
            <img className="h-10 w-auto" src={ramsaLogoShort} alt="Ramsalab" />
          </div>
          <h1 className="relative z-10 mt-8 text-4xl font-bold tracking-tight">Dictionary</h1>
          {searchField}
          {!searchQuery.trim() && (
            <div className="relative z-10 mt-6 flex flex-wrap items-center justify-center gap-2">
              <span className="text-sm text-white/80">Popular searches:</span>
              {SEARCH_EXAMPLES.map((example) => (
                <button
                  key={example.query}
                  type="button"
                  onClick={() => setSearchQuery(example.query)}
                  className="rounded-md bg-white/20 px-3 py-1 text-sm text-white hover:bg-white/30"
                  title={formatExampleHint(example.hint)}
                >
                  <span dir="auto">{example.query}</span>
                </button>
              ))}
            </div>
          )}
        </section>
      ) : (
        <>
          <header className="px-4 py-4 text-white" style={TOPBAR_BACKGROUND}>
            <div className="mx-auto flex max-w-5xl items-center gap-4">
              <button type="button" onClick={() => navigate("/demo_dictionary")} aria-label="Dictionary home">
                <img className="h-10 w-auto" src={ramsaLogoShort} alt="Ramsalab" />
              </button>
              {searchField}
            </div>
          </header>
          <main className="mx-auto max-w-3xl px-4 py-8 text-left sm:px-6 lg:px-8">{children}</main>
        </>
      )}
    </div>
  );
}
